import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Home, MoreHorizontal } from "lucide-react";

/** Q45 universal breadcrumb. One row, left-aligned, sits above the
 *  page content (and below any L2 rail header). NOT sticky so the
 *  vertical space is preserved.
 *
 *  Visual contract:
 *    - Separator: subtle chevron (›) in muted slate
 *    - Clickable segments: muted slate by default, hover -> primary
 *      slate with underline
 *    - Active (current) segment: solid primary slate, not a link
 *    - Optional 14px icon per segment, before the label
 *    - Home root: prepended automatically (Home icon) unless
 *      `home={false}` is passed
 *    - Narrow viewport (<md): if total segments > `truncate`, the
 *      middle segments collapse into a tappable … popover. Desktop
 *      always shows every segment.
 *
 *  Usage:
 *    <Breadcrumb segments={[
 *      { label: "People", href: "/explore/people" },
 *      { label: person.name },
 *    ]} />
 *
 *  Coordinates with sister tasks:
 *    - L2 vertical rail (local_f6c47487): the breadcrumb is rendered
 *      INSIDE the L2RailShell's main content area, above any page
 *      header. Multi-pane Q37 pages put it above both panes.
 *    - Travel epic (local_91c81a8e), People (local_6fd0daed),
 *      Deep-link viewer (local_d7ec82c3): consume the same component
 *      so the trail stays consistent everywhere.
 */
export interface BreadcrumbSegment {
  label: string;
  href?: string;
  icon?: ReactNode;
}

interface BreadcrumbProps {
  segments: BreadcrumbSegment[];
  /** Prepend a Home root that links to `/`. Defaults true. */
  home?: boolean;
  /** Collapse middle segments into a … popover on narrow viewports
   *  when the total count exceeds this. Defaults 4 (Home + 3). */
  truncate?: number;
  className?: string;
}

const HOME_SEGMENT: BreadcrumbSegment = {
  label: "Home",
  href: "/",
  icon: <Home className="w-3.5 h-3.5" strokeWidth={2.25} />,
};

export function Breadcrumb({
  segments,
  home = true,
  truncate = 4,
  className = "",
}: BreadcrumbProps) {
  const navigate = useNavigate();
  const all: BreadcrumbSegment[] = home
    ? [HOME_SEGMENT, ...segments]
    : segments;

  // Back parent = the penultimate segment when there's at least one
  // real ancestor beyond Home. Rule: show Back only when total
  // segments >= 3 (Home + parent + current) AND the parent has an
  // href. /today, /explore, /engagements (root view) therefore skip
  // Back; /explore/library/orgs, /people/:id, /settings/:section
  // show it. Same hierarchy as the breadcrumb itself + the L1/L2
  // rails — they reinforce, not duplicate.
  const parent = useMemo<BreadcrumbSegment | null>(() => {
    if (all.length < 3) return null;
    const candidate = all[all.length - 2];
    return candidate.href ? candidate : null;
  }, [all]);

  // ESC -> parent. Scoped so it doesn't clobber form inputs, native
  // overlays (dialog/alertdialog), or our own popover menus (the
  // CollapsedMenu has its own Escape handler; when its [role=menu]
  // is mounted we skip).
  useEffect(() => {
    if (!parent || !parent.href) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (e.defaultPrevented) return;
      const ae = document.activeElement as HTMLElement | null;
      const tag = (ae?.tagName ?? "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (ae?.isContentEditable) return;
      if (
        document.querySelector(
          '[role="dialog"][aria-modal="true"], [role="alertdialog"], [role="menu"]',
        )
      )
        return;
      e.preventDefault();
      navigate(parent.href!);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [parent, navigate]);

  if (all.length === 0) return null;

  const collapseMobile = all.length > truncate;

  const renderFull = (items: BreadcrumbSegment[]) =>
    items.map((seg, i) => (
      <Fragment key={`${i}-${seg.label}`}>
        {i > 0 && <Separator />}
        <SegmentNode segment={seg} isCurrent={i === items.length - 1} />
      </Fragment>
    ));

  return (
    <nav
      aria-label="Breadcrumb"
      className={`text-xs sm:text-[13px] min-w-0 ${className}`}
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        {parent && (
          <>
            <BackButton href={parent.href!} parentLabel={parent.label} />
            <span
              aria-hidden="true"
              className="h-4 w-px bg-slate-300 dark:bg-slate-700 flex-shrink-0"
            />
          </>
        )}
        {collapseMobile ? (
          <>
            <ol className="hidden md:flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
              {renderFull(all)}
            </ol>
            <ol className="flex md:hidden flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
              <SegmentNode segment={all[0]} />
              <Separator />
              <CollapsedMenu items={all.slice(1, -2)} />
              <Separator />
              <SegmentNode segment={all[all.length - 2]} />
              <Separator />
              <SegmentNode segment={all[all.length - 1]} isCurrent />
            </ol>
          </>
        ) : (
          <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
            {renderFull(all)}
          </ol>
        )}
      </div>
    </nav>
  );
}

/** Paired back affordance. Navigates to the breadcrumb's parent
 *  route via React Router (not browser history, which can land
 *  outside the app or jump tabs). Icon-only on narrow viewports;
 *  icon + "Back" label on >=sm. */
function BackButton({
  href,
  parentLabel,
}: {
  href: string;
  parentLabel: string;
}) {
  return (
    <Link
      to={href}
      aria-label={`Back to ${parentLabel}`}
      title={`Back to ${parentLabel} (Esc)`}
      className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 -ml-1 rounded-md text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
    >
      <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
      <span className="hidden sm:inline text-xs font-medium">Back</span>
    </Link>
  );
}

function Separator() {
  return (
    <li
      aria-hidden="true"
      className="flex items-center text-slate-400 dark:text-slate-600 select-none"
    >
      <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.25} />
    </li>
  );
}

function SegmentNode({
  segment,
  isCurrent = false,
}: {
  segment: BreadcrumbSegment;
  isCurrent?: boolean;
}) {
  const inner = (
    <span className="inline-flex items-center gap-1 min-w-0 max-w-[16ch] sm:max-w-[24ch] md:max-w-none">
      {segment.icon && (
        <span className="flex-shrink-0 inline-flex">{segment.icon}</span>
      )}
      <span className="truncate">{segment.label}</span>
    </span>
  );

  if (isCurrent || !segment.href) {
    return (
      <li
        aria-current={isCurrent ? "page" : undefined}
        className={
          isCurrent
            ? "font-semibold text-slate-900 dark:text-slate-100 min-w-0"
            : "text-slate-600 dark:text-slate-300 min-w-0"
        }
      >
        {inner}
      </li>
    );
  }

  return (
    <li className="min-w-0">
      <Link
        to={segment.href}
        className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-300 hover:underline underline-offset-4 decoration-violet-500/60 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
      >
        {inner}
      </Link>
    </li>
  );
}

function CollapsedMenu({ items }: { items: BreadcrumbSegment[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <li ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Show ${items.length} hidden breadcrumb segments`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-6 h-6 rounded-md text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60"
      >
        <MoreHorizontal className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 min-w-[160px] z-30 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg py-1"
        >
          {items.map((seg, i) =>
            seg.href ? (
              <Link
                key={`m-${i}-${seg.label}`}
                role="menuitem"
                to={seg.href}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/60"
              >
                {seg.icon}
                <span className="truncate">{seg.label}</span>
              </Link>
            ) : (
              <span
                key={`m-${i}-${seg.label}`}
                role="menuitem"
                className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400"
              >
                {seg.icon}
                <span className="truncate">{seg.label}</span>
              </span>
            ),
          )}
        </div>
      )}
    </li>
  );
}
